import React from "react";
import { connect } from "react-redux";
import {
    Layout, Table, Button, Row, Col, Space, Radio, Input, Tooltip,
    Modal, Typography, notification
} from "antd";
import {
    SaveOutlined, DollarCircleOutlined, FileExcelOutlined
} from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";
import {
    Header, InputCurrency, ColumnEmployee, CardCompanyBalance, CardInfo, BoxColor
} from "components";
import { useNavigate } from "react-router-dom";
import { getCorporateCardEmployees } from "services/card-service";
import { getAllBenefitCharge } from "services/benefit-service";
import { formatMoney } from "utils";
import { read, utils, writeFile } from "xlsx";

const { Footer, Content } = Layout;
const { Text } = Typography;

const INDIVIDUAL = "individual";
const UPLOAD = "upload";

const mapStateToProps = (state) => ({
    login: state.authReducer.loginReducer,
    balance: state.balanceReducer,
});

const SolicitationCorporateScreen = (props) => {
    const { login, balance } = props;
    const navigate = useNavigate();

    const [benefits, setBenefits] = React.useState([]);
    const [data, setData] = React.useState([]);
    const [uploadEmployees, setUploadEmployees] = React.useState([]);
    const [filterEmployees, setFilterEmployees] = React.useState([]);
    const [filterUploadEmployees, setFilterUploadEmployees] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [option, setOption] = React.useState(INDIVIDUAL);
    const [searchValue, setSearchValue] = React.useState("");
    const [showImportModal, setShowImportModal] = React.useState(false);
    const [uploadErrors, setUploadErrors] = React.useState([]);
    const [showConfirmOrder, setShowConfirmOrder] = React.useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = React.useState(false);
    const [isConfirmLoading, setIsConfirmLoading] = React.useState(false);
    const [applyAll, setApplyAll] = React.useState({ show: false, title: "", benefit: null, value: 0 });

    const cardStyle = { minHeight: 160, textAlign: "center" };

    const buildData = (employees, bens) =>
        employees.map(emp => ({
            employee: emp,
            ...Object.fromEntries(bens.map(b => [b.key, { value: 0, benefit: b.benefit, name: b.name }])),
        }));

    const load = async () => {
        setLoading(true);
        const [employees, res] = await Promise.all([
            getCorporateCardEmployees(),
            getAllBenefitCharge(login.company.id),
        ]);

        const loadedBenefits = (res.benefits || [])
            .filter(b => b.enabled)
            .map(b => ({ ...b, key: `benefit_${b.benefit}` }));

        setBenefits(loadedBenefits);
        const built = buildData(employees, loadedBenefits);
        setData(built);
        setFilterEmployees(built);
        setLoading(false);
    };

    React.useEffect(() => { load(); }, []);

    // ---------- helpers ----------

    const calculateTotal = (arr) =>
        arr.reduce((total, emp) => {
            if (!emp.employee) return total;
            return total + Object.values(emp).reduce((sum, item) => {
                const v = item?.value ?? 0;
                return sum + (v > 0 ? v : 0);
            }, 0);
        }, 0);

    const calculateTotalBenefitFree = (arr) => {
        const FREE_ID = -1;
        return arr.reduce((total, emp) => {
            if (!emp.employee) return total;
            return total + Object.entries(emp)
                .filter(([, v]) => v?.benefit === FREE_ID)
                .reduce((sum, [, item]) => sum + (item?.value ?? 0), 0);
        }, 0);
    };

    const countParticipants = (list) =>
        list.filter(row =>
            Object.values(row)
                .filter(v => v && "value" in v)
                .some(v => v.value > 0)
        ).length;

    const getParticipants = (list) =>
        list.filter(row =>
            Object.values(row)
                .filter(v => v && "value" in v)
                .some(v => v.value > 0)
        );

    const getChargeBenefitFree = (amount) =>
        ((login.company.corporateChargeFee ?? 0) / 100) * amount;

    const sumTotal = React.useMemo(
        () => calculateTotal(option === INDIVIDUAL ? data : uploadEmployees),
        [data, uploadEmployees, option]
    );

    const sumTotalFee = React.useMemo(
        () => calculateTotalBenefitFree(option === INDIVIDUAL ? data : uploadEmployees),
        [data, uploadEmployees, option]
    );

    // ---------- edit / filter ----------

    const onEditRow = (key, value, employeeId) => {
        const setter = option === INDIVIDUAL ? setData : setUploadEmployees;
        const filterSetter = option === INDIVIDUAL ? setFilterEmployees : setFilterUploadEmployees;
        setter(prev => {
            const next = prev.map(row =>
                row.employee.id === employeeId
                    ? { ...row, [key]: { ...row[key], value: value ?? 0 } }
                    : row
            );
            return next;
        });
        filterSetter(prev =>
            prev.map(row =>
                row.employee.id === employeeId
                    ? { ...row, [key]: { ...row[key], value: value ?? 0 } }
                    : row
            )
        );
    };

    const onFilter = (value) => {
        const lowercased = value.toLowerCase();
        const source = option === INDIVIDUAL ? data : uploadEmployees;
        const filtered = source.filter(row => {
            const onlyDigits = value.replace(/\D/g, "");
            const cpf = (row.employee?.cpf || "").replace(/\D/g, "");
            const name = (row.employee?.name || "").toLowerCase();
            const email = (row.employee?.email || "").toLowerCase();
            const matchesText = name.includes(lowercased) || email.includes(lowercased);
            const matchesDoc = onlyDigits && cpf.includes(onlyDigits);
            return matchesText || matchesDoc;
        });
        option === INDIVIDUAL
            ? setFilterEmployees(filtered)
            : setFilterUploadEmployees(filtered);
    };

    // ---------- apply all ----------

    const applyAllFunctions = {
        onShowApplyAll: (show, benefit = null) => {
            setApplyAll({ show, title: benefit ? `Aplicar a todos: ${benefit.name}` : "", benefit, value: 0 });
        },
        onConfirmApplyAll: () => {
            const source = option === INDIVIDUAL ? data : uploadEmployees;
            const updated = source.map(row => ({
                ...row,
                [applyAll.benefit.key]: { ...row[applyAll.benefit.key], value: applyAll.value }
            }));
            if (option === INDIVIDUAL) {
                setData(updated);
                setFilterEmployees(updated);
            } else {
                setUploadEmployees(updated);
                setFilterUploadEmployees(updated);
            }
            applyAllFunctions.onShowApplyAll(false);
        },
    };

    // ---------- upload ----------

    const formatCPF = (cpf) => cpf ? String(cpf).replace(/\D/g, "").padStart(11, "0") : "";

    const handleDownloadFile = () => {
        const rows = data.map(row => {
            const entry = {
                "Nome / Razão Social (opicional)": row.employee.name || "",
                "CPF / CNPJ (obrigatório)": row.employee.cpf || "",
            };
            benefits.forEach(b => { entry[b.name] = row[b.key]?.value || 0; });
            return entry;
        });
        const ws = utils.json_to_sheet(rows);
        ws["!cols"] = rows[0]
            ? Object.keys(rows[0]).map(() => ({ wch: 30 }))
            : [{ wch: 30 }, { wch: 30 }, { wch: 15 }];
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Data");
        writeFile(wb, `${(login.company.name || "empresa").toLowerCase()}_carga_corporativa.xlsx`);
    };

    const handleUploadFile = async (file) => {
        setLoading(true);
        const fileReader = new FileReader();
        fileReader.onerror = () => {
            notification.error({ message: "Erro ao ler o arquivo." });
            setLoading(false);
        };
        fileReader.onload = (event) => {
            try {
                const workbook = read(event.target.result, { type: "array" });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const fileHeaders = utils.sheet_to_json(worksheet, { header: 1 })[0];
                const sheetData = utils.sheet_to_json(worksheet);
                const expected = [
                    "Nome / Razão Social (opicional)",
                    "CPF / CNPJ (obrigatório)",
                    ...benefits.map(b => b.name),
                ];
                if (!expected.every(h => fileHeaders.includes(h))) {
                    notification.error({ message: "Formato incorreto", description: "Cabeçalho não corresponde ao esperado." });
                    setLoading(false);
                    return;
                }

                setUploadErrors([]);
                const processed = new Set();
                const errors = [];

                const updatedData = sheetData.reduce((acc, row) => {
                    const cpf = formatCPF(row["CPF / CNPJ (obrigatório)"]);
                    const matching = data.find(d => formatCPF(d.employee.cpf) === cpf);
                    if (!matching) {
                        errors.push({ ...row, Erro: "CPF não cadastrado" });
                        return acc;
                    }
                    if (processed.has(cpf)) {
                        errors.push({ ...row, Erro: "CPF em duplicidade" });
                        return acc;
                    }
                    const hasCharge = benefits.some(b => (row[b.name] ?? 0) > 0);
                    if (!hasCharge) {
                        errors.push({ ...row, Erro: "Nenhuma carga informada" });
                        return acc;
                    }
                    processed.add(cpf);
                    const updated = { ...matching };
                    benefits.forEach(b => {
                        let v = row[b.name];
                        if (typeof v === "string") v = parseFloat(v.replace(".", "").replace(",", "."));
                        updated[b.key] = { ...updated[b.key], value: Number(parseFloat(v || 0).toFixed(2)) };
                    });
                    acc.push(updated);
                    return acc;
                }, []);

                setUploadErrors(errors);
                setUploadEmployees(updatedData);
                setFilterUploadEmployees(updatedData);

                if (updatedData.length === 0) {
                    notification.info({ message: "Arquivo sem valor de carga", description: "Preencha os valores antes de continuar." });
                } else {
                    setShowImportModal(false);
                }
            } catch {
                notification.error({ message: "Erro ao processar o arquivo." });
            } finally {
                setLoading(false);
            }
        };
        fileReader.readAsArrayBuffer(file);
        return false;
    };

    const exportErrors = () => {
        if (uploadErrors.length === 0) return;
        const ws = utils.json_to_sheet(uploadErrors);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Erros");
        writeFile(wb, "erros_carga_corporativa.xlsx");
    };

    // ---------- columns ----------

    const columns = React.useMemo(() => {
        const cols = [{
            fixed: "left",
            width: 250,
            key: "name",
            dataIndex: "employee",
            title: "Participante",
            render: v => <ColumnEmployee name={v.name} email={v.email} cpf={v.cpf} />,
        }];
        benefits.forEach(b => {
            cols.push({
                align: "right",
                width: 310,
                key: b.benefit,
                dataIndex: b.key,
                title: () => (
                    <Row align="middle" justify="center">
                        <Col><BoxColor color={b.color} /></Col>
                        <Col style={{ marginLeft: 10 }}>
                            <Space>
                                {b.name}
                                <Tooltip placement="bottom" title={`Aplicar valor para todos: ${b.name}`}>
                                    <DollarCircleOutlined
                                        style={{ fontSize: 20 }}
                                        onClick={() => applyAllFunctions.onShowApplyAll(true, b)}
                                    />
                                </Tooltip>
                            </Space>
                        </Col>
                    </Row>
                ),
                render: (value, record) => (
                    <InputCurrency
                        value={value?.value ?? 0}
                        onChange={v => onEditRow(b.key, v, record.employee.id)}
                    />
                ),
            });
        });
        return cols;
    }, [benefits, data, uploadEmployees, option]);

    // ---------- revision ----------

    const createRevision = async () => {
        const source = option === INDIVIDUAL ? data : uploadEmployees;

        const createLines = () => {
            const lines = [];
            source.forEach(row => {
                benefits.forEach(b => {
                    const v = row[b.key]?.value ?? 0;
                    if (v > 0) lines.push({ user: row.employee.id, benefit: b.benefit, value: v });
                });
            });
            return lines;
        };

        const createCheckout = async () => {
            const total = sumTotal;
            const corporateFee = total * ((login.company.corporateChargeFee ?? 0) / 100);
            const corporatePartFee = countParticipants(source) * (login.company.corporatePartChargeFee ?? 0);
            const grandTotal = total + corporateFee + corporatePartFee;
            const available = balance?.company?.available ?? 0;
            return [
                { label: "Valor", value: total },
                { label: "Taxa Administrativa", value: corporateFee },
                { label: "Taxa por Participante", value: corporatePartFee },
                { total: true, discount: true, hasPayment: total > available, label: "Valor Total", value: grandTotal },
                { total: true, discount: false, hasPayment: total > 0, label: "Valor Total", value: grandTotal },
            ];
        };

        return {
            request: { url: "/solicitation/corporate", method: "post" },
            body: { lines: createLines() },
            checkout: await createCheckout(),
            type: "corporate",
            successMessage: "Pedido corporativo realizado com sucesso.",
        };
    };

    const onConfirm = () => {
        if (option === UPLOAD && uploadErrors.length > 0) {
            setShowConfirmOrder(true);
        } else {
            setIsConfirmModalVisible(true);
        }
    };

    // ---------- render ----------

    return (
        <Layout className="alymente-layout">
            <Header title="Crédito para Gastos Corporativos">
                <Row gutter={12} align="top">
                    <Col xl={4}>
                        <CardCompanyBalance
                            size="medium"
                            style={cardStyle}
                            info="Saldo em conta da empresa."
                            type="balance"
                        />
                    </Col>
                    <Col xl={4}>
                        <CardInfo
                            style={cardStyle}
                            size="medium"
                            info="Valor a ser descontado por este pedido."
                            title="Valor deste Pedido"
                            value={formatMoney(sumTotal || 0)}
                        />
                    </Col>
                    <Col xl={4}>
                        <CardInfo
                            style={cardStyle}
                            size="medium"
                            info="Valor a ser descontado x a quantidade de participantes."
                            title="Valor da taxa por participante"
                            value={formatMoney(login.company.corporatePartChargeFee || 0)}
                        />
                    </Col>
                    <Col xl={4}>
                        <CardInfo
                            style={cardStyle}
                            size="medium"
                            info="Valor a ser descontado por este pedido."
                            title="Valor da taxa por segmentação"
                            value={formatMoney(getChargeBenefitFree(sumTotalFee || 0))}
                        />
                    </Col>
                    <Col xl={4}>
                        <CardInfo
                            size="medium"
                            style={cardStyle}
                            info="Quantidade de participantes deste pedido de carga."
                            title="Qtd. Participantes"
                            value={String(countParticipants(option === INDIVIDUAL ? data : uploadEmployees) || "")}
                        />
                    </Col>
                    <Col xl={4} style={{ textAlign: "center" }}>
                        {option === UPLOAD && (
                            <Button block icon={<FileExcelOutlined />} type="primary" onClick={() => setShowImportModal(true)}>
                                Importar
                            </Button>
                        )}
                    </Col>
                </Row>
            </Header>
            <Content>
                <Table
                    rowKey={row => row.employee?.id}
                    loading={loading}
                    columns={columns}
                    dataSource={option === INDIVIDUAL ? filterEmployees : filterUploadEmployees}
                    scroll={{ x: "max-content", y: 450 }}
                    bordered
                    title={() => (
                        <Row align="bottom" justify="center" style={{ margin: 0, padding: 0 }}>
                            <Col span={8} />
                            <Col span={8} style={{ textAlign: "center" }}>
                                <Radio.Group
                                    disabled={loading}
                                    options={[
                                        { label: "Seleção Individual", value: INDIVIDUAL },
                                        { label: "Upload", value: UPLOAD },
                                    ]}
                                    onChange={e => setOption(e.target.value)}
                                    value={option}
                                    optionType="button"
                                    buttonStyle="solid"
                                />
                            </Col>
                            <Col span={8} style={{ textAlign: "center" }}>
                                <Input
                                    allowClear
                                    value={searchValue}
                                    onChange={e => { setSearchValue(e.target.value); onFilter(e.target.value); }}
                                    placeholder="Buscar por Nome, E-mail ou CPF"
                                />
                            </Col>
                        </Row>
                    )}
                />
                {option === UPLOAD && (
                    <Row style={{ marginTop: 20 }}>
                        <Col sm={8} style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginBottom: 10 }}>
                            <Button
                                disabled={uploadErrors.length === 0}
                                style={{ marginRight: 10 }}
                                size="small"
                                type="primary"
                                onClick={exportErrors}
                            >
                                Exportar erros
                            </Button>
                            <Text>Erros: {uploadErrors.length}</Text>
                        </Col>
                    </Row>
                )}
            </Content>
            <Footer style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Button type="primary" onClick={() => navigate(-1)}>Voltar</Button>
                <Button
                    disabled={sumTotal === 0 && (option === INDIVIDUAL || uploadEmployees.length === 0)}
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={onConfirm}
                >
                    Fazer Pedido
                </Button>
            </Footer>

            {/* Confirmação antes de revisão */}
            <Modal
                width={400}
                open={isConfirmModalVisible}
                title="Deseja continuar?"
                okText="Sim"
                cancelText="Não"
                confirmLoading={isConfirmLoading}
                onOk={async () => {
                    setIsConfirmLoading(true);
                    try {
                        const users = getParticipants(option === INDIVIDUAL ? data : uploadEmployees);
                        const revisionData = await createRevision();
                        navigate("/solicitation/corporate/revision", {
                            state: { data: revisionData, users, benefits, _data: [...data] }
                        });
                    } finally {
                        setIsConfirmLoading(false);
                        setIsConfirmModalVisible(false);
                    }
                }}
                onCancel={() => setIsConfirmModalVisible(false)}
            >
                <p>Após a confirmação, você irá fazer a revisão do seu pedido.</p>
            </Modal>

            {/* Confirmação com erros de upload */}
            <Modal
                width={400}
                open={showConfirmOrder}
                title="Deseja continuar?"
                okText="Sim"
                cancelText="Não"
                onOk={() => { setShowConfirmOrder(false); setIsConfirmModalVisible(true); }}
                onCancel={() => setShowConfirmOrder(false)}
            >
                <p><strong>{uploadErrors.length} participantes não receberão carga</strong>, para entender o porquê, exporte os erros.</p>
            </Modal>

            {/* Aplicar valor a todos */}
            {applyAll.show && (
                <Modal
                    width={400}
                    title={applyAll.title}
                    open={applyAll.show}
                    onOk={applyAllFunctions.onConfirmApplyAll}
                    onCancel={() => applyAllFunctions.onShowApplyAll(false)}
                >
                    <InputCurrency
                        value={applyAll.value}
                        onChange={value => setApplyAll(prev => ({ ...prev, value }))}
                    />
                </Modal>
            )}

            {/* Modal de importação */}
            <Modal
                width={500}
                open={showImportModal}
                title="Importar planilha"
                footer={null}
                onCancel={() => setShowImportModal(false)}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Row gutter={8} justify="end">
                        <Col>
                            <Button size="small" onClick={() => handleDownloadFile()}>
                                Baixar modelo preenchido
                            </Button>
                        </Col>
                        <Col>
                            <Button size="small" onClick={() => {
                                const empty = buildData([], benefits);
                                const rows = [{
                                    "Nome / Razão Social (opicional)": "",
                                    "CPF / CNPJ (obrigatório)": "",
                                    ...Object.fromEntries(benefits.map(b => [b.name, 0])),
                                }];
                                const ws = utils.json_to_sheet(rows);
                                ws["!cols"] = rows[0] ? Object.keys(rows[0]).map(() => ({ wch: 30 })) : [];
                                const wb = utils.book_new();
                                utils.book_append_sheet(wb, ws, "Data");
                                writeFile(wb, "modelo_carga_corporativa.xlsx");
                            }}>
                                Baixar modelo vazio
                            </Button>
                        </Col>
                    </Row>
                    <Dragger
                        accept=".xlsx"
                        beforeUpload={handleUploadFile}
                        showUploadList={false}
                        multiple={false}
                    >
                        <p className="ant-upload-drag-icon">
                            <FileExcelOutlined style={{ fontSize: 32, color: "#52c41a" }} />
                        </p>
                        <p className="ant-upload-text">Clique ou arraste o arquivo aqui</p>
                        <p className="ant-upload-hint">Suporte apenas para arquivos .xlsx</p>
                    </Dragger>
                </Space>
            </Modal>
        </Layout>
    );
};

export default connect(mapStateToProps)(SolicitationCorporateScreen);
